import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

import { LastSignInNotice } from '@/features/my-account/components/LastSignInNotice';

/**
 * Simpler than merchant-dashboard's own AppShell: no `useActiveShop()` (no
 * shop concept on this app's routes at all), no command palette (two real
 * screens do not need one) — `useTheme()` is called once, directly inside
 * `Topbar`, since there is no second instance here to desync against.
 *
 * Below `md` the sidebar becomes a drawer opened from the Topbar's menu
 * button; it closes on every navigation so the chosen screen is what shows.
 */
export function AppShell(): ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  return (
    <div className="flex h-dvh w-full bg-ground">
      <Sidebar mobileOpen={menuOpen} onCloseMobile={closeMenu} />
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          menuOpen={menuOpen}
          onOpenMenu={() => {
            setMenuOpen(true);
          }}
        />
        <LastSignInNotice />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
